import {Injectable} from '@angular/core';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import * as ReportActions from '../actions/report.actions';
import * as Reducers from '../reducers';
import { catchError, concatMap, exhaustMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { EMPTY, forkJoin, interval, of } from 'rxjs';
import {RecurringReportsService} from '../../../reports/services/recurring-reports.service';
import {ReportsService} from '../../../reports/services/reports.service';
import { Store } from '@ngrx/store';
import { cacheLifeTime } from '../../../shared/config';
import { DataHelper } from '../../../shared/helpers/data.helper';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { ReportCode } from '../../../reports/models/report-code';

@Injectable()
export class ReportsEffects {

  getRecurringReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.getRecurringReports.type),
      switchMap((action: any) => this.recurringReportsService.getRecurringReports()
        .pipe(
          exhaustMap((data) => of(ReportActions.getRecurringReportsSuccess({recurringReports: data}))),
          catchError(error => of(ReportActions.getRecurringReportsFailure({error})))
        )
      )
    )
  );

  createRecurringReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.createRecurringReport.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this.recurringReportsService.createRecurringReports(action['recurringReport'])
        .pipe(
          exhaustMap((data) => of(ReportActions.createRecurringReportSuccess({recurringReport: data}))),
          catchError(error => of(ReportActions.createRecurringReportFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  deleteRecurringReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.deleteRecurringReport.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this.recurringReportsService.deleteRecurringReports(action['id'])
        .pipe(
          exhaustMap((data) => of(ReportActions.deleteRecurringReportSuccess({id: action['id']}))),
          catchError(error => of(ReportActions.deleteRecurringReportFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  editRecurringReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.editRecurringReport.type),
      tap(() => this._loader.start()),
      switchMap((action: any) =>
        forkJoin([
          of(action),
          this.recurringReportsService.deleteRecurringReports(action.oldId),
        ])
      ),
      concatMap((action: any) => {
        return this.recurringReportsService.createRecurringReports(action[0].recurringReport).pipe(
          exhaustMap((data) => of(ReportActions.editRecurringReportSuccess({oldId: action[0].oldId, recurringReport: data}))),
          catchError(error => of(ReportActions.editRecurringReportFailure({error}))),
          tap(() => this._loader.complete()),
        );
      })
    )
  );


  getReportFileTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.getReportFileTypes.type),
      withLatestFrom(
        this.store$.select(Reducers.getAllReportFileTypes)
      ),
      concatMap(([action, fileTypes]) => {
        const requests = [];
        (<any>action['reportGeneratorIds']).forEach(x => {
          if (DataHelper.isNotEmpty(x) && (!fileTypes.has(x) || action['renew'])) {
            requests.push(this.reportsService.getRequestReportFileTypes(x));
          }
        });
        if (requests.length > 0) {
          return forkJoin(requests)
            .pipe(
              exhaustMap((data) => of(ReportActions.getReportFileTypesSuccess({reportsFileTypes: data}))),
              catchError(error => of(ReportActions.getReportFileTypesFailure({error}))),
              tap(() => this._loader.complete()),
            );
        } else {
          this._loader.complete();
          return EMPTY;
        }
      })
    )
  );


  schedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.schedule.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this.reportsService.schedule(action['reportParams'])
        .pipe(
          exhaustMap((data) => of(ReportActions.scheduleSuccess({reportParams: data}))),
          catchError(error => of(ReportActions.scheduleFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );


  getReportsHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.getReportsHistory.type),
      switchMap((action: any) => this.reportsService.getReportsHistory(action.params)
        .pipe(
          exhaustMap((data) => of(ReportActions.getReportsHistorySuccess({reports: data}))),
          catchError(error => of(ReportActions.getReportsHistoryFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  @Effect()
  clear = interval(cacheLifeTime).pipe(
    switchMap((action) => of(ReportActions.clearReportFileTypes())),
    switchMap(() =>  of(ReportActions.getReportFileTypes({reportGeneratorIds: Object.values(ReportCode), renew: true})))
  );

  private _loader: LoadingBarState;

  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private recurringReportsService: RecurringReportsService,
    private reportsService: ReportsService,
    private _loadingBar: LoadingBarService
  ) {
    this._loader = this._loadingBar.useRef('main');
  }
}
